import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, BarChart3, Download, Play, Library } from 'lucide-react';
import { reports } from '@/lib/api';
import { Link } from 'react-router-dom';

export default function ReportsPage() {
  const [filters, setFilters] = React.useState({
    matter_type: '', subclass: '', status: '', assignee: '',
    date_from: '', date_to: '', include_archived: false,
  });
  const [running, setRunning] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v }));

  const cleanFilters = () => {
    const p = { ...filters };
    Object.keys(p).forEach((k) => { if (p[k] === '' || p[k] === null) delete p[k]; });
    return p;
  };

  const run = async () => {
    setRunning(true);
    try { setResult(await reports.run(cleanFilters())); }
    finally { setRunning(false); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const blob = await reports.exportXlsx(cleanFilters());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ImmiPro-Matters-Report.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  React.useEffect(() => { run(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto max-w-7xl p-8" data-testid="reports-page">
      <div className="flex items-center gap-3">
        <BarChart3 className="h-6 w-6 text-[#2ECC9A]" />
        <h1 className="text-2xl font-serif font-bold text-[#1A3A5C]">Reports</h1>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Build user-defined reports across your firm&rsquo;s matters. Export to Excel for
        board packs, OMARA compliance reviews or fee reconciliation.
      </p>
      <Link to="/app/reports/library">
        <Card className="mt-4 border-emerald-200 bg-emerald-50/60 cursor-pointer hover:border-emerald-300 transition" data-testid="reports-library-link">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="rounded-full bg-emerald-100 p-2 text-emerald-700"><Library className="h-5 w-5" /></div>
            <div>
              <div className="font-semibold text-[#1A3A5C]">Reports library</div>
              <div className="text-xs text-slate-600">Board-ready canned reports: revenue-by-RMA, matter aging, ready-to-lodge, VEVO expiry, retention — CSV &amp; PDF export</div>
            </div>
          </CardContent>
        </Card>
      </Link>

      <Card className="mt-6 border-slate-200">
        <CardContent className="p-4">
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Subclass</Label>
              <Input data-testid="report-subclass" value={filters.subclass}
                     onChange={(e) => setF('subclass', e.target.value)}
                     placeholder="189, 482, 500…" className="mt-1 h-9 text-xs font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Status</Label>
              <Select value={filters.status || 'any'} onValueChange={(v) => setF('status', v === 'any' ? '' : v)}>
                <SelectTrigger data-testid="report-status" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="lodged">Lodged</SelectItem>
                  <SelectItem value="granted">Granted</SelectItem>
                  <SelectItem value="refused">Refused</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
              <Input data-testid="report-date-from" type="date" value={filters.date_from}
                     onChange={(e) => setF('date_from', e.target.value)} className="mt-1 h-9 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
              <Input data-testid="report-date-to" type="date" value={filters.date_to}
                     onChange={(e) => setF('date_to', e.target.value)} className="mt-1 h-9 text-xs" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input data-testid="report-archived" type="checkbox"
                     checked={filters.include_archived}
                     onChange={(e) => setF('include_archived', e.target.checked)} />
              Include archived matters
            </label>
            <div className="ml-auto flex items-center gap-2">
              <Button data-testid="report-run" onClick={run} disabled={running}
                      className="h-9 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
                {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Play className="h-3 w-3" /> Run report</>}
              </Button>
              <Button data-testid="report-export" onClick={exportXlsx} disabled={exporting || !result?.rows?.length}
                      variant="outline"
                      className="h-9 gap-1 border-[#2ECC9A]/40 bg-[#2ECC9A]/5 text-[#0F766E] text-xs">
                {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Download className="h-3 w-3" /> Export .xlsx</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card className="mt-6 border-slate-200">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-[#1A3A5C]">Results</div>
              <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500">
                {result.count} matter{result.count === 1 ? '' : 's'}
              </Badge>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-xs" data-testid="report-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                    {result.columns.map((c) => <th key={c.key} className="px-3 py-2">{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      {result.columns.map((c) => (
                        <td key={c.key} className="px-3 py-2 text-[#1A3A5C]">
                          {typeof r[c.key] === 'boolean' ? (r[c.key] ? '✓' : '') : (r[c.key] || '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {result.rows.length === 0 && (
                    <tr><td colSpan={result.columns.length} className="px-3 py-8 text-center text-slate-400">
                      No matters match your filters
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
